import React from "react";
import { Facebook, Instagram, Music2, Phone, Mail } from "lucide-react";
import Logo from "@/components/Logo";

export default function Footer({ data, site, services = [] }) {
  const socials = [
    { Icon: Facebook, href: site?.social?.facebook || "#", label: "Facebook" },
    { Icon: Instagram, href: site?.social?.instagram || "#", label: "Instagram" },
    { Icon: Music2, href: site?.social?.tiktok || "#", label: "TikTok" },
  ];
  const servicesList = data?.services_list && data.services_list.length
    ? data.services_list
    : services.filter((s) => s.enabled !== false).map((s) => s.title);

  return (
    <footer data-testid="site-footer" className="relative bg-[#0A0A0A] text-white pt-16 pb-28 lg:pb-16 px-6">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center text-center">
          <Logo size={88} variant="mark" />
          <h3 className="mt-5 font-display text-2xl sm:text-3xl font-bold text-white">
            {site?.name?.split(" ")[0] || "M&C"}{" "}
            <span className="text-gradient-gold">{(site?.name || "").split(" ").slice(1).join(" ") || "Clean Fresh"}</span>
          </h3>
          {data?.tagline && (
            <p className="mt-2 text-sm text-white/55 max-w-md">{data.tagline}</p>
          )}
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10 text-center sm:text-left">
          <div>
            <h4 className="font-display text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-5">Services</h4>
            <ul className="space-y-2.5">
              {servicesList.map((s, i) => (
                <li key={`${s}-${i}`}>
                  <a href="#services" className="text-sm text-white/65 hover:text-[#D4AF37] transition-colors">{s}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-5">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a href={site?.phone_href || "#"} data-testid="footer-phone" className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-[#D4AF37] transition-colors">
                  <Phone className="w-4 h-4" /> {site?.phone}
                </a>
              </li>
              <li>
                <a href={site?.email_href || "#"} data-testid="footer-email" className="inline-flex items-center gap-2 text-sm text-white/65 hover:text-[#D4AF37] transition-colors break-all">
                  <Mail className="w-4 h-4" /> {site?.email}
                </a>
              </li>
              <li className="text-sm text-white/55">Greater Boston, MA</li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-5">Follow</h4>
            <div className="flex justify-center sm:justify-start gap-3">
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label} href={href} aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 transition-all flex items-center justify-center group"
                >
                  <Icon className="w-4 h-4 text-white/70 group-hover:text-[#D4AF37] transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/10 text-center">
          <p className="text-xs text-white/45 tracking-wide" data-testid="footer-copyright">
            {data?.copyright || "© 2026 M&C Clean Fresh. All Rights Reserved."}
          </p>
          <p className="mt-2 text-xs text-white/40 tracking-wide" data-testid="footer-powered-by">
            Powered by{" "}
            <a
              href="https://www.SallyBetter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:text-[#E6C75A] underline-offset-2 hover:underline transition-colors"
              data-testid="footer-powered-by-link"
            >
              www.SallyBetter.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
